import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';

import { createHiringRequestFromConnectWithNewEndpoint } from './hiringRequestQueries';
import {
  CandidateApplicationConnectData,
  EnhancedHiringRequestResponse,
} from '../types/hiringRequestTypes';
import { hiringRequestQueries } from 'queryKeys/hiringRequest';
import { UpdateHiringRequestResponseData } from '../types';
import { AxiosError } from 'axios';
import { UpdateHiringRequestVariables } from 'features/References';
import hiringRequestsAPI from 'api/hiringRequestsAPI';

export function useCreateHiringRequestNewEndpointMutation(
  districtId,
  options: Omit<
    UseMutationOptions<
      EnhancedHiringRequestResponse,
      unknown,
      CandidateApplicationConnectData,
      unknown
    >,
    'mutationFn'
  > = {}
): UseMutationResult<
  EnhancedHiringRequestResponse,
  unknown,
  CandidateApplicationConnectData,
  unknown
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload) => {
      const response = await createHiringRequestFromConnectWithNewEndpoint(payload);

      return {
        candidate_id: response.candidate_id,
        hiring_request_id: response.hiring_request_id,
        internal_candidate_note_content: payload.internal_candidate_note_content,
        schools: payload.schools,
        contact_methods: payload.contact_methods,
      };
    },
    ...options,
    onSuccess: (data, variables, context) => {
      const queryKey = hiringRequestQueries.hiringRequests.list(districtId).queryKey;

      queryClient.invalidateQueries({
        queryKey,
        refetchType: 'all',
        exact: true,
      });
      options.onSuccess?.(data, variables, context);
    },
  });
}

export function useMutationUpdateHiringRequest(
  districtId: number,
  options: Omit<
    UseMutationOptions<
      UpdateHiringRequestResponseData,
      AxiosError,
      UpdateHiringRequestVariables,
      undefined
    >,
    'mutationFn'
  > = {}
): UseMutationResult<
  UpdateHiringRequestResponseData,
  AxiosError,
  UpdateHiringRequestVariables,
  undefined
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ hiringRequestId, payload }) =>
      hiringRequestsAPI.updateHiringRequest(hiringRequestId, payload),
    ...options,
    onSuccess: (data, variables, context) => {
      options.onSuccess?.(data, variables, context);

      queryClient.invalidateQueries({
        queryKey: hiringRequestQueries.hiringRequests.list(districtId).queryKey,
        refetchType: 'all',
        exact: true,
      });
    },
  });
}
